import React, { FC, useState, ReactNode, useMemo, useEffect } from "react";
import { Input, Button, Tabs, Empty, Spin, Cascader, Tag } from "antd";
import {
  SearchOutlined,
  HistoryOutlined,
  BookOutlined,
  ArrowLeftOutlined,
  HeartOutlined,
  PushpinFilled,
  PushpinOutlined,
} from "@ant-design/icons";
import styles from "./scope-and-adjustments-library.module.css";
import { LibraryListRenderer, ScopeLibrary } from "./library-renderers";
import { SubTabType, LibraryItem, TabItemMap } from "./types/library-item-types";
import { useMatterAssumptionsQuery } from "~/graphql/query/matter-assumptions-query";
import { useMatterExclusionsQuery } from "~/graphql/query/matter-exclusions-query";
import {
  Matter,
  PracticeArea,
  RelevantMatter,
  orgUnitCascaderValue,
  orgUnitFromCascadeValue,
} from "@design-system";
import { VPDLabel } from "~/context/taxonomy-context";
const {
  "scope-library": scopeLibraryClassName,
  "scope-library-header": scopeLibraryHeaderClassName,
  "scope-library-search": scopeLibrarySearchClassName,
  "scope-library-content": scopeLibraryContentClassName,
  "scope-library-section": scopeLibrarySectionClassName,
  "scope-library-tabs": scopeLibraryTabsClassName,
  "scope-library-tabs-bar": scopeLibraryTabsBarClassName,
  "practice-area-filter-section": practiceAreaFilterSectionClassName,
  "practice-area-cascader": practiceAreaCascaderClassName,
  "matter-details": matterDetailsClassName,
  "matter-title-container": matterTitleContainerClassName,
  "matter-back-button": matterBackButtonClassName,
  "template-tag": templateTagClassName,
  "matter-empty-data-alert": matterEmptyDataAlertClassName,
  "clickable-title": clickableTitleClassName,
  "view-more-link": viewMoreLinkClassName,
} = styles as any;

export interface LibraryItemRenderer<T> {
  items: T[] | Record<string, T[]>;
  selectedItems?: T[];
  loading?: boolean;
  onSelectItem: (item: T) => void;
  searchText: string;
  activeTab?: "library" | "historical" | "favourites";
  selectedMatter?: Matter | null;
  onSelectMatter?: (matterId: number | null, matterType?: MatterType) => void;
  practiceAreas?: PracticeArea[];
  matterOrgUnitId?: number | null;
  selectedOrgUnitId?: number | null;
  setSelectedOrgUnitId?: (orgUnitId: number | null) => void;
}

type SidebarTabType = "library" | "historical" | "favourites";
export type MatterType = "template" | "historical" | "favourite";

export type SelectedMatterInfo = {
  matterId: number | null;
  matter: Matter | null;
  matterType: MatterType | null;
  viewingData: boolean;
  loading?: boolean;
  onTitleClick?: () => void;
};

export type ScopeAndAdjustmentsLibraryProps = {
  activeSubTab: SubTabType;
  organisationalUnitId?: number | null;
  onSelectItem?: (item: LibraryItem) => void;
  onCopyPhases: (matterId: number, phaseId?: number) => void;

  // Search text props
  searchTextState: {
    librarySearchText: string; // For library items in non-scope tabs
    setLibrarySearchText: (text: string) => void;
    matterSearchText: string; // Persistent search for templates and historical matters
    setMatterSearchText: (text: string) => void;
  };

  selectedMatterInfo?: SelectedMatterInfo;
  onSelectMatter?: (matterId: number | null, matterType?: MatterType) => void;
  onClearSelectedMatter?: () => void;

  matterData: {
    historical?: {
      data?: RelevantMatter[];
      loading?: boolean;
    };
    favourites?: {
      data?: RelevantMatter[];
      loading?: boolean;
    };
    templates?: {
      loading?: boolean;
    };
    pinned?: {
      data?: RelevantMatter[];
      loading?: boolean;
    };
  };

  // Generic library renderers
  libraryRenderers?: {
    [key in SubTabType]?: (props: LibraryItemRenderer<any>) => ReactNode;
  };
  libraryItems?: {
    [key in SubTabType]?: any[] | Record<string, any[]>;
  };
  selectedLibraryItems?: {
    [key in SubTabType]?: any[];
  };

  // Practice area related props
  practiceAreas?: PracticeArea[];
  selectedOrgUnitId?: number | null;
  setSelectedOrgUnitId?: (orgUnitId: number | null) => void;

  currencySymbol: string;

  // Pagination props
  pagination?: {
    enabled?: boolean;
    currentPage?: number;
    pageSize?: number;
    totalCount?: number;
    onPageChange?: (page: number, pageSize: number) => void;
  };

  onPinToMatter?: (matterId: number) => void;
};

export const ScopeAndAdjustmentsLibrary: FC<ScopeAndAdjustmentsLibraryProps> = ({
  activeSubTab,
  organisationalUnitId,
  onSelectItem,
  onCopyPhases,
  searchTextState,
  matterData,
  selectedMatterInfo,
  onSelectMatter,
  onClearSelectedMatter,
  libraryRenderers,
  libraryItems,
  selectedLibraryItems,
  selectedOrgUnitId,
  setSelectedOrgUnitId,
  currencySymbol,
  pagination,
  practiceAreas,
  onPinToMatter,
}) => {
  const PA_LABEL = VPDLabel("PracticeArea", { textCase: "lower" });
  const TEMPLATE_LABEL = VPDLabel("Template", { textCase: "lower" });
  const TEMPLATE_LABEL_TITLE = VPDLabel("Template", { textCase: "title" });

  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTabType>("library");

  const [prevSubTab, setPrevSubTab] = useState<SubTabType>(activeSubTab);

  // Reset to library tab only when sub tab changes and no matter is selected
  useEffect(() => {
    if (activeSubTab !== prevSubTab && !selectedMatterInfo?.matterId) {
      setActiveSidebarTab("library");
    }
    setPrevSubTab(activeSubTab);
  }, [activeSubTab, prevSubTab, selectedMatterInfo?.matterId]);

  // Memoize skip conditions for matter data
  const shouldSkipAssumptions = useMemo(
    () =>
      !selectedMatterInfo?.matterId ||
      !selectedMatterInfo?.viewingData ||
      activeSubTab !== "assumptions",
    [selectedMatterInfo?.matterId, selectedMatterInfo?.viewingData, activeSubTab]
  );

  const shouldSkipExclusions = useMemo(
    () =>
      !selectedMatterInfo?.matterId ||
      !selectedMatterInfo?.viewingData ||
      activeSubTab !== "exclusions",
    [selectedMatterInfo?.matterId, selectedMatterInfo?.viewingData, activeSubTab]
  );

  // Fetch assumptions and exclusions for the selected matter
  const { data: assumptionsData } = useMatterAssumptionsQuery(
    shouldSkipAssumptions ? 0 : selectedMatterInfo?.matterId || 0
  );
  const { data: exclusionsData } = useMatterExclusionsQuery(
    shouldSkipExclusions ? 0 : selectedMatterInfo?.matterId || 0
  );

  // Store the fetched data
  const selectedMatterAssumptions = shouldSkipAssumptions
    ? []
    : assumptionsData?.matterAssumptions || [];
  const selectedMatterExclusions = shouldSkipExclusions
    ? []
    : exclusionsData?.matterExclusions || [];

  // Check if we have a custom renderer for the current sub-tab
  const hasCustomRenderer = libraryRenderers && libraryRenderers[activeSubTab];
  const currentTabItems = libraryItems && libraryItems[activeSubTab];
  const currentSelectedItems = selectedLibraryItems && selectedLibraryItems[activeSubTab];

  // Handle selecting a matter
  const handleSelectMatter = (matterId: number | null, matterType?: MatterType) => {
    if (onSelectMatter) {
      onSelectMatter(matterId, matterType || getMatterTypeFromSidebarTab(activeSidebarTab));
    }
  };

  // Helper to determine matter type from sidebar tab
  const getMatterTypeFromSidebarTab = (tab: SidebarTabType): MatterType => {
    switch (tab) {
      case "library":
        return "template";
      case "historical":
        return "historical";
      case "favourites":
        return "favourite";
      default:
        return "template";
    }
  };

  // When the sidebar tab changes, we should show the library content for that tab
  const handleSidebarTabChange = (key: string) => {
    setActiveSidebarTab(key as SidebarTabType);
    onClearSelectedMatter?.();
  };

  // Function to render practice area filter
  const renderPracticeAreaFilter = () => {
    if (!practiceAreas || practiceAreas.length === 0) return null;

    // Only show the filter in the scope tab OR in historical/favourites tab for ANY sub-tab
    if (
      activeSubTab !== "scope" &&
      activeSidebarTab !== "historical" &&
      activeSidebarTab !== "favourites"
    )
      return null;

    const paOptions = practiceAreas
      .filter((x) => !x.parent?.id)
      .map((pa) => {
        return {
          value: pa.id,
          label: pa.name,
          children: pa.children?.map((y) => ({ value: y.id, label: y.name })) || [],
        };
      });

    return (
      <div className={practiceAreaFilterSectionClassName} style={{ marginBottom: "16px" }}>
        <div className={practiceAreaCascaderClassName}>
          <Cascader
            style={{ width: "100%" }}
            allowClear={true}
            showSearch={true}
            placeholder={`Filter by ${PA_LABEL}`}
            value={
              selectedOrgUnitId !== null
                ? orgUnitCascaderValue(practiceAreas, selectedOrgUnitId)
                : undefined
            }
            options={paOptions}
            onChange={(val: any) => {
              const orgUnitValue = val && val.length > 0 ? orgUnitFromCascadeValue(val) : null;
              setSelectedOrgUnitId?.(orgUnitValue);
            }}
          />
        </div>
      </div>
    );
  };

  // Render the header with a back button when viewing matter data
  const renderLibraryHeader = () => {
    if (selectedMatterInfo?.viewingData && selectedMatterInfo?.matter) {
      const isTemplate = selectedMatterInfo.matterType === "template";
      const isHistorical = selectedMatterInfo.matterType === "historical";
      const isPinned = matterData.pinned?.data?.some(
        (matter) => matter.id === selectedMatterInfo.matter?.id
      );

      return (
        <div className={scopeLibraryHeaderClassName}>
          <div className={matterTitleContainerClassName}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onClearSelectedMatter}
              className={matterBackButtonClassName}
            />
            {isHistorical && selectedMatterInfo.onTitleClick ? (
              <h2 className={clickableTitleClassName} onClick={selectedMatterInfo.onTitleClick}>
                {selectedMatterInfo.matter.title}
                <span className={viewMoreLinkClassName}>(More info)</span>
              </h2>
            ) : (
              <h2>{selectedMatterInfo.matter.title}</h2>
            )}
          </div>
          <div className={matterDetailsClassName}>
            {selectedMatterInfo.matter.client?.name && (
              <>
                {selectedMatterInfo.matter.client.name}
                {selectedMatterInfo.matter.organisationalUnit && (
                  <span> | {selectedMatterInfo.matter.organisationalUnit.name}</span>
                )}
              </>
            )}
            {!selectedMatterInfo.matter.client?.name &&
              selectedMatterInfo.matter.organisationalUnit && (
                <span>{selectedMatterInfo.matter.organisationalUnit.name}</span>
              )}
            {isTemplate && (
              <Tag color="blue" className={templateTagClassName} style={{ marginLeft: "16px" }}>
                {TEMPLATE_LABEL_TITLE}
              </Tag>
            )}
          </div>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              marginTop: "8px",
            }}
          >
            <h2 style={{ margin: 0 }}>
              {activeSubTab.charAt(0).toUpperCase() + activeSubTab.slice(1)}
            </h2>
            {isHistorical && onPinToMatter && selectedMatterInfo.matter?.id && (
              <Button
                type={isPinned ? "primary" : "default"}
                icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                onClick={() =>
                  selectedMatterInfo.matter?.id && onPinToMatter(selectedMatterInfo.matter.id)
                }
                size="small"
              >
                {isPinned ? "Unpin" : "Pin"}
              </Button>
            )}
          </div>
        </div>
      );
    }

    // Determine which search state to use based on context
    const isMatterSearch =
      activeSubTab === "scope" ||
      activeSidebarTab === "historical" ||
      activeSidebarTab === "favourites";
    const currentSearchText = isMatterSearch
      ? searchTextState.matterSearchText
      : searchTextState.librarySearchText;
    const setCurrentSearchText = isMatterSearch
      ? searchTextState.setMatterSearchText
      : searchTextState.setLibrarySearchText;

    let headerTitle = "";
    if (activeSidebarTab === "library") {
      if (activeSubTab === "scope") {
        headerTitle = `${TEMPLATE_LABEL_TITLE} Library`;
      } else {
        headerTitle = `${activeSubTab.charAt(0).toUpperCase() + activeSubTab.slice(1)} Library`;
      }
    } else if (activeSidebarTab === "historical") {
      headerTitle = "Historical Matters";
    } else if (activeSidebarTab === "favourites") {
      headerTitle = "Favourite Matters";
    }

    // Show practice area filter for scope tab or historical tab
    const shouldShowFilter =
      activeSidebarTab !== "favourites" &&
      (activeSubTab === "scope" || activeSidebarTab === "historical");

    return (
      <div className={scopeLibraryHeaderClassName}>
        <h2>{headerTitle}</h2>
        <Input
          placeholder={
            activeSidebarTab === "library"
              ? `Search ${activeSubTab === "scope" ? `${TEMPLATE_LABEL}s` : activeSubTab}...`
              : `Search ${activeSidebarTab} matters...`
          }
          prefix={<SearchOutlined style={{ color: "#1890ff" }} />}
          value={currentSearchText}
          onChange={(e) => setCurrentSearchText(e.target.value)}
          className={scopeLibrarySearchClassName}
          allowClear
        />
        {shouldShowFilter && renderPracticeAreaFilter()}
      </div>
    );
  };

  // Render library content based on active sub-tab and sidebar tab
  const renderLibraryContent = () => {
    // If we're viewing a selected matter's data for non-scope tabs
    if (selectedMatterInfo?.viewingData && selectedMatterInfo?.matter && activeSubTab !== "scope") {
      return renderSelectedMatterContent();
    }

    // For library scope tab and historical/favourites tabs, use ScopeLibrary component
    if (
      (activeSubTab === "scope" && activeSidebarTab === "library") ||
      activeSidebarTab === "historical" ||
      activeSidebarTab === "favourites"
    ) {
      let matterDataToUse;
      let loadingState;
      let onSelectMatterHandler;
      let pinnedMattersToUse: RelevantMatter[] = [];

      if (activeSubTab === "scope" && activeSidebarTab === "library") {
        // For scope tab with library active
        matterDataToUse = libraryItems?.scope as RelevantMatter[];
        loadingState = matterData.templates?.loading;
        onSelectMatterHandler = (id: number | null) => handleSelectMatter(id, "template");
        pinnedMattersToUse = [];
      } else {
        // For historical or favourites tab
        matterDataToUse =
          activeSidebarTab === "historical"
            ? matterData.historical?.data
            : matterData.favourites?.data;
        loadingState =
          activeSidebarTab === "historical"
            ? matterData.historical?.loading
            : matterData.favourites?.loading;
        onSelectMatterHandler = (id: number | null) =>
          handleSelectMatter(id, getMatterTypeFromSidebarTab(activeSidebarTab));

        // Only include pinned matters for historical tab
        pinnedMattersToUse = activeSidebarTab === "historical" ? matterData.pinned?.data || [] : [];
      }

      return (
        <div className={scopeLibraryContentClassName}>
          <div className={scopeLibrarySectionClassName}>
            <ScopeLibrary
              matterData={matterDataToUse}
              mattersLoading={loadingState}
              selectedMatterId={selectedMatterInfo?.matter?.id}
              selectedMatter={selectedMatterInfo?.matter}
              onSelectMatter={onSelectMatterHandler}
              onCopyPhases={onCopyPhases}
              activeTab={activeSidebarTab}
              pagination={pagination}
              pinnedMatters={pinnedMattersToUse}
            />
          </div>
        </div>
      );
    }

    // Library tab content for non-scope tabs
    if (activeSidebarTab === "library") {
      // Use the provided custom renderer for all tabs
      if (hasCustomRenderer && currentTabItems) {
        const renderer = libraryRenderers?.[activeSubTab];
        if (renderer) {
          // Determine which search text to use
          const searchTextToUse = searchTextState.librarySearchText;

          return (
            <div className={scopeLibraryContentClassName}>
              <div className={scopeLibrarySectionClassName}>
                {renderer({
                  items: currentTabItems,
                  selectedItems: currentSelectedItems,
                  loading: false,
                  onSelectItem: onSelectItem || (() => {}),
                  searchText: searchTextToUse,
                  activeTab: activeSidebarTab,
                  selectedMatter: selectedMatterInfo?.matter,
                  onSelectMatter: handleSelectMatter,
                  practiceAreas: practiceAreas,
                  matterOrgUnitId: organisationalUnitId,
                  selectedOrgUnitId: selectedOrgUnitId,
                  setSelectedOrgUnitId: setSelectedOrgUnitId,
                })}
              </div>
            </div>
          );
        }
      }

      // Fallback if no renderer is available
      return (
        <div className={scopeLibraryContentClassName}>
          <div className={scopeLibrarySectionClassName}>{renderDefaultEmptyLibrary()}</div>
        </div>
      );
    }

    // Default empty state
    return (
      <div className={scopeLibraryContentClassName}>
        <div className={scopeLibrarySectionClassName}>{renderDefaultEmptyLibrary()}</div>
      </div>
    );
  };

  // Render content specific to the selected matter based on activeSubTab
  const renderSelectedMatterContent = () => {
    if (!selectedMatterInfo?.matter) return null;

    // Show a loading state when fetching matter details
    if (selectedMatterInfo?.loading) {
      return (
        <div className={scopeLibraryContentClassName}>
          <div className={scopeLibrarySectionClassName}>
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin tip="Loading data..." />
            </div>
          </div>
        </div>
      );
    }

    // Check if we have the data for this sub-tab
    const hasData = () => {
      switch (activeSubTab) {
        case "assumptions":
          return selectedMatterAssumptions && selectedMatterAssumptions.length > 0;
        case "exclusions":
          return selectedMatterExclusions && selectedMatterExclusions.length > 0;
        case "disbursements":
          return (
            selectedMatterInfo?.matter?.disbursements &&
            selectedMatterInfo?.matter?.disbursements.length > 0
          );
        case "uplifts":
          return (
            selectedMatterInfo?.matter?.uplifts && selectedMatterInfo?.matter?.uplifts.length > 0
          );
        case "discounts":
          return (
            selectedMatterInfo?.matter?.discounts &&
            selectedMatterInfo?.matter?.discounts.length > 0
          );
        case "scope":
          return (
            selectedMatterInfo?.matter?.phases && selectedMatterInfo?.matter?.phases.length > 0
          );
        default:
          return false;
      }
    };

    // For scope tab, we keep the existing behavior
    if (activeSubTab === "scope") {
      if (!hasData()) {
        return (
          <div className={scopeLibraryContentClassName}>
            <div className={scopeLibrarySectionClassName}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <p>
                      No {activeSubTab} data available for this{" "}
                      {selectedMatterInfo.matterType === "template" ? "template" : "matter"}
                    </p>
                    <Button
                      type="primary"
                      onClick={onClearSelectedMatter}
                      style={{ marginTop: "12px" }}
                    >
                      View Library Items
                    </Button>
                  </div>
                }
              />
            </div>
          </div>
        );
      }

      // For scope, we don't need to prepare items since the scope tab
      // has its own special renderer that doesn't use items
      return (
        <div className={scopeLibraryContentClassName}>
          <div className={scopeLibrarySectionClassName}>
            {/* For the scope tab, we don't display items directly */}
          </div>
        </div>
      );
    }

    // For all other tabs, we show both selected items and library items

    // Prepare the selected items based on activeSubTab
    let selectedItems: TabItemMap[typeof activeSubTab][] = [];

    switch (activeSubTab) {
      case "assumptions":
        // Clone the items and remove id to avoid conflicts
        selectedItems = selectedMatterAssumptions.map((item) => ({
          ...item,
          id: undefined,
        }));
        break;
      case "exclusions":
        selectedItems = selectedMatterExclusions.map((item) => ({
          ...item,
          id: undefined,
        }));
        break;
      case "disbursements":
        selectedItems = (selectedMatterInfo?.matter?.disbursements || []).map((item) => ({
          ...item,
          id: undefined,
        }));
        break;
      case "uplifts":
        selectedItems = (selectedMatterInfo?.matter?.uplifts || []).map((item) => ({
          ...item,
          id: undefined,
        }));
        break;
      case "discounts":
        selectedItems = (selectedMatterInfo?.matter?.discounts || []).map((item) => ({
          ...item,
          id: undefined,
        }));
        break;
      default:
        selectedItems = [];
    }

    // Check if we have a custom renderer and items for the library section
    const hasCustomRenderer = libraryRenderers && libraryRenderers[activeSubTab];
    const currentTabItems = libraryItems && libraryItems[activeSubTab];

    return (
      <div className={scopeLibraryContentClassName}>
        {/* Selected Matter Items Section */}
        <div className={scopeLibrarySectionClassName}>
          {!hasData() ? (
            <div className={matterEmptyDataAlertClassName}>
              <span>
                No {activeSubTab} found for the selected{" "}
                {selectedMatterInfo.matterType === "template" ? "template" : "matter"}.
              </span>
            </div>
          ) : (
            <>
              <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>
                {selectedMatterInfo.matterType === "template" ? "Template" : "Matter"} Items
              </h4>
              <LibraryListRenderer
                items={selectedItems}
                selectedItems={selectedLibraryItems?.[activeSubTab] || []}
                loading={false}
                onSelectItem={(item) => onSelectItem?.({ ...item, id: -1 })}
                searchText={searchTextState.librarySearchText}
                subTab={activeSubTab}
                currencySymbol={selectedMatterInfo?.matter?.currency?.symbol || currencySymbol}
                historical={selectedMatterInfo?.matterType !== "template"}
              />
            </>
          )}
        </div>

        {/* Library Items Section - Always shown for non-scope tabs */}
        {hasCustomRenderer && currentTabItems && (
          <div className={scopeLibrarySectionClassName} style={{ marginTop: "24px" }}>
            <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>Library Items</h4>
            {libraryRenderers[activeSubTab]!({
              items: currentTabItems,
              selectedItems: currentSelectedItems,
              loading: false,
              onSelectItem: onSelectItem || (() => {}),
              searchText: searchTextState.librarySearchText,
              activeTab: activeSidebarTab,
              selectedMatter: selectedMatterInfo?.matter,
              onSelectMatter: handleSelectMatter,
              practiceAreas: practiceAreas,
              matterOrgUnitId: organisationalUnitId,
              selectedOrgUnitId: selectedOrgUnitId,
              setSelectedOrgUnitId: setSelectedOrgUnitId,
            })}
          </div>
        )}
      </div>
    );
  };

  // Helper function to render a default empty state
  const renderDefaultEmptyLibrary = () => {
    // Determine which search text to use
    const searchTextToUse =
      activeSubTab === "scope" ||
      activeSidebarTab === "historical" ||
      activeSidebarTab === "favourites"
        ? searchTextState.matterSearchText
        : searchTextState.librarySearchText;

    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div>
            <p>No items found</p>
            {searchTextToUse && <small>Try adjusting your search terms</small>}
          </div>
        }
      />
    );
  };

  // Define the tabs for the sidebar
  const tabItems = [
    {
      key: "library",
      label: (
        <span>
          <BookOutlined />
          Library
        </span>
      ),
    },
    {
      key: "historical",
      label: (
        <span>
          <HistoryOutlined />
          Historical
        </span>
      ),
    },
    {
      key: "favourites",
      label: (
        <span>
          <HeartOutlined />
          Favourites
        </span>
      ),
    },
  ];

  return (
    <div className={scopeLibraryClassName}>
      {/* Always show tabs */}
      <div className={scopeLibraryTabsClassName}>
        <Tabs
          activeKey={activeSidebarTab}
          onChange={handleSidebarTabChange}
          items={tabItems}
          size="small"
          tabBarStyle={{ marginBottom: 0, marginLeft: 20 }}
          renderTabBar={(props, DefaultTabBar) => (
            <DefaultTabBar {...props} className={scopeLibraryTabsBarClassName} />
          )}
        />
      </div>

      {renderLibraryHeader()}
      {renderLibraryContent()}
    </div>
  );
};
